import { useLazyQuery, useMutation } from "@apollo/client";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { CustomGridColDef, DataGrid } from "components/DataGrid";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { SubmissionStatus } from "generated-graphql/graphql";
import { DateTime } from "luxon";
import { useEffect, useMemo, useState } from "react";
import {
  BULK_SUBMISSIONS_SAFE_TO_UPDATE_QUERY,
  BULK_UPDATE_SUBMISSIONS_STATUS_MUTATION,
} from "queries/submissions";
import { Row } from "./WasteSubmissionsDataGrid";
import { partitionSubmissions, formatVirtualEventForGrid } from "./utils";

const STATUS_LABELS: Record<string, string> = {
  NOT_STARTED: "Not Started",
  IN_PROGRESS: "In Progress",
  ENCAMP_INITIAL_REVIEW: "Initial Review",
  IN_REVIEW: "In Review",
  PROCESSING_SUBMISSION: "Processing",
  FILING_COMPLETE: "Complete",
  NOT_REPORTING: "Not Reporting",
};

interface ChangeStatusDialogProps {
  open: boolean;
  onClose: () => void;
  submissions: Row[];
  onSuccess?: () => void;
}

export function ChangeStatusDialog({
  open,
  onClose,
  submissions,
  onSuccess,
}: ChangeStatusDialogProps) {
  const alerts = useAlerts();
  const [selectedStatus, setSelectedStatus] = useState<SubmissionStatus | "">(
    ""
  );

  // Partition virtual events from real submissions
  const { virtualEvents, uniqueSubmissionIds, virtualEventIds } = useMemo(
    () => partitionSubmissions(submissions),
    [submissions]
  );

  const [
    checkSafety,
    { data: safetyData, loading: checkingLoading, error: safetyError },
  ] = useLazyQuery(BULK_SUBMISSIONS_SAFE_TO_UPDATE_QUERY);

  const [bulkUpdateSubmissionsStatus, { loading: mutationLoading }] =
    useMutation(BULK_UPDATE_SUBMISSIONS_STATUS_MUTATION, {
      refetchQueries: ["WasteSubmissionsForStaff", "WasteSubmissionsStaffSummary"],
      awaitRefetchQueries: true,
      onCompleted: (data) => {
        if (data.bulkUpdateSubmissionsStatus.succeeded) {
          alerts.success(
            `Job queued to update submission(s). Job ID: ${data.bulkUpdateSubmissionsStatus.jobId}`
          );
          handleClose();
          onSuccess?.();
        }
      },
      onError: (error) => {
        alerts.error(`Failed to queue submission update job: ${error.message}`);
      },
    });

  const loading = checkingLoading || mutationLoading;

  useEffect(() => {
    if (open && selectedStatus && uniqueSubmissionIds.length > 0) {
      checkSafety({
        variables: {
          submissionIds: uniqueSubmissionIds,
          targetStatus: selectedStatus,
        },
      });
    }
  }, [open, selectedStatus, uniqueSubmissionIds, checkSafety]);

  const handleClose = () => {
    setSelectedStatus("");
    onClose();
  };

  const handleStatusChange = (event: SelectChangeEvent<SubmissionStatus>) => {
    setSelectedStatus(event.target.value as SubmissionStatus);
  };

  const { safeSubmissions, unsafeSubmissions, virtualEventRows } =
    useMemo(() => {
      const safe = safetyData?.bulkSubmissionsSafeToUpdate
        ? safetyData.bulkSubmissionsSafeToUpdate.filter(
            (item: { isSafe: boolean }) => item.isSafe
          )
        : [];
      const unsafe = safetyData?.bulkSubmissionsSafeToUpdate
        ? safetyData.bulkSubmissionsSafeToUpdate.filter(
            (item: { isSafe: boolean }) => !item.isSafe
          )
        : [];

      const virtualRows = virtualEvents.map(formatVirtualEventForGrid);

      return {
        safeSubmissions: safe,
        unsafeSubmissions: unsafe,
        virtualEventRows: virtualRows,
      };
    }, [safetyData, virtualEvents]);

  const handleSubmit = async () => {
    if (
      !selectedStatus ||
      (safeSubmissions.length === 0 && virtualEventIds.length === 0)
    ) {
      return;
    }

    await bulkUpdateSubmissionsStatus({
      variables: {
        input: {
          submissionIds: safeSubmissions.map((s: { id: string }) => s.id),
          virtualEventIds: virtualEventIds,
          status: selectedStatus,
        },
      },
    });
  };

  const allowedStatuses = [
    {
      value: SubmissionStatus.InProgress,
      label: STATUS_LABELS[SubmissionStatus.InProgress],
    },
    {
      value: SubmissionStatus.EncampInitialReview,
      label: STATUS_LABELS[SubmissionStatus.EncampInitialReview],
    },
    {
      value: SubmissionStatus.NotReporting,
      label: STATUS_LABELS[SubmissionStatus.NotReporting],
    },
  ];

  const columns: CustomGridColDef<any>[] = [
    {
      field: "dueDate",
      headerName: "Due Date",
      width: 100,
      valueGetter: (value, row) =>
        row.submission?.dueDate
          ? DateTime.fromISO(row.submission.dueDate).toFormat("MM/dd/yyyy")
          : "",
    },
    {
      field: "facility",
      headerName: "Facility",
      flex: 1,
      minWidth: 120,
      valueGetter: (value, row) => {
        if (row.isVirtual) {
          return (
            row.facilities?.map((f: { name: string }) => f.name).join(", ") ||
            ""
          );
        }
        return (
          row.submission?.facilitySubmissions
            ?.map((fs: { facility: { name: string } }) => fs.facility.name)
            .join(", ") || ""
        );
      },
    },
    {
      field: "submissionType",
      headerName: "Submission Type",
      width: 130,
      valueGetter: (value, row) =>
        prettyPrintEnumValue(row.submission?.submissionType),
    },
    {
      field: "title",
      headerName: "Title",
      flex: 1,
      minWidth: 150,
      valueGetter: (value, row) => row.submission?.title,
    },
    {
      field: "currentStatus",
      headerName: "Current Status",
      width: 140,
      valueGetter: (value, row) =>
        row.isVirtual
          ? "Virtual Event"
          : STATUS_LABELS[row.submission?.status] || row.submission?.status,
    },
    {
      field: "updatedStatus",
      headerName: "Updated Status",
      width: 140,
      valueGetter: () => (selectedStatus ? STATUS_LABELS[selectedStatus] : ""),
    },
  ];

  const blockedColumns: CustomGridColDef<any>[] = [
    ...columns.slice(0, -1),
    {
      field: "reason",
      headerName: "Reason Blocked",
      flex: 1,
      minWidth: 250,
      valueGetter: (value, row) => row.reasons?.join("; ") || "",
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      slotProps={{
        paper: {
          sx: {
            width: 800,
            maxWidth: "90vw",
          },
        },
      }}
    >
      <DialogTitle>
        Change Status for {uniqueSubmissionIds.length + virtualEventIds.length}{" "}
        Submission(s)
        {virtualEventIds.length > 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {virtualEventIds.length} virtual event(s) will be converted to real
            submissions
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to update the status of the selected{" "}
            {uniqueSubmissionIds.length + virtualEventIds.length} submission(s)?
          </Typography>

          <FormControl fullWidth>
            <InputLabel id="status-select-label">Target Status *</InputLabel>
            <Select
              labelId="status-select-label"
              id="status-select"
              value={selectedStatus}
              label="Target Status *"
              onChange={handleStatusChange}
              disabled={loading}
            >
              {allowedStatuses.map((status) => (
                <MenuItem key={status.value} value={status.value}>
                  {status.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {selectedStatus &&
            checkingLoading &&
            uniqueSubmissionIds.length > 0 && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Checking which submissions can be updated...
                </Typography>
              </Box>
            )}

          {selectedStatus && safetyError && (
            <Alert severity="error">
              Failed to check submission status: {safetyError.message}
            </Alert>
          )}

          {selectedStatus &&
            !checkingLoading &&
            (uniqueSubmissionIds.length > 0 ? safetyData : true) && (
              <Box>
                {(safeSubmissions.length > 0 ||
                  virtualEventRows.length > 0) && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Submissions to Update (
                      {safeSubmissions.length + virtualEventRows.length})
                    </Typography>
                    <DataGrid
                      rows={[...safeSubmissions, ...virtualEventRows]}
                      columns={columns}
                      getRowId={(row) => row.id}
                      disableRowSelectionOnClick
                      pagination
                      paginationMode="client"
                      pageSizeOptions={[5, 10]}
                      hideFooterPagination={false}
                      initialState={{
                        pagination: {
                          paginationModel: { pageSize: 10 },
                        },
                      }}
                      sx={{ maxHeight: 600 }}
                    />
                  </Box>
                )}

                {unsafeSubmissions.length > 0 && (
                  <Box>
                    <Alert
                      severity={
                        safeSubmissions.length === 0 &&
                        virtualEventRows.length === 0
                          ? "error"
                          : "warning"
                      }
                      sx={{ mb: 1 }}
                    >
                      {safeSubmissions.length === 0 &&
                      virtualEventRows.length === 0
                        ? "None of the selected submissions can be updated."
                        : `${unsafeSubmissions.length} submission(s) cannot be updated and will be skipped`}
                    </Alert>
                    <DataGrid
                      rows={unsafeSubmissions}
                      columns={blockedColumns}
                      getRowId={(row) => row.id}
                      disableRowSelectionOnClick
                      pagination
                      paginationMode="client"
                      pageSizeOptions={[5, 10]}
                      hideFooterPagination={false}
                      initialState={{
                        pagination: {
                          paginationModel: { pageSize: 10 },
                        },
                      }}
                      sx={{ maxHeight: 600 }}
                    />
                  </Box>
                )}
              </Box>
            )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={
            loading ||
            !selectedStatus ||
            (safeSubmissions.length === 0 && virtualEventRows.length === 0)
          }
        >
          {checkingLoading
            ? "Checking..."
            : mutationLoading
            ? "Updating..."
            : "Confirm"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
